import classnames from 'classnames';
import { innerJoin, map } from 'ramda';
import React, { FC, memo, useCallback } from 'react';
import { ContextMenuTrigger, FullHeightLoader } from '../../../components/common';
import { Lasso } from '../../../components/common/Lasso';
import { BoardsLayout } from '../../../components/layouts/Boards';
import { GalleryLayout } from '../../../components/layouts/Gallery';
import { ListLayout } from '../../../components/layouts/List';
import { ContextMenuId } from '../../../constants/ContextMenuId';
import { LassoItemMargin } from '../../../constants/Lasso';
import { Layout } from '../../../constants/Layout';
import { useTrackActions } from '../../../utils/hooks/bi/useTrackActions';
import { ActionClickOrigin, ActionTarget } from '../../../utils/hooks/bi/useTrackActions/useTrackActions.types';
import { useTrackItemSelectAnalytics } from '../../../utils/hooks/bi/useTrackItemSelectAnalytics';
import { ItemSelectOrigin } from '../../../utils/hooks/bi/useTrackItemSelectAnalytics/types';
import { useBoolean } from '../../../utils/hooks/useBoolean';
import { useLoadingErrorCatcher } from '../../../utils/hooks/useLoadingErrorCatcher';
import { useSources } from '../../../utils/hooks/useSources';
import { MediaManagerSDKFile, MediaManagerSDKFolder } from '../../../utils/media-manager-sdk';
import { isBoard } from '../../../utils/media-manager-sdk/boards';
import { isFile, isItemFavorite } from '../../../utils/media-manager-sdk/items';
import { EmptyState } from '../EmptyState';
import { ExtendedItemsFilters } from '../ExtendedItemsFilters';
import styles from '../FilesManager.scss';
import { IOuter } from './SourceContent.types';

const contextMenuTriggerAttributes = { className: styles.layoutWrapper };

const SourceContentComponent: FC<IOuter> = ({
  items,
  selectedItems,
  editingItem,
  source,
  searchQuery,
  currentFolder,
  settingsLayout,
  publicMediaCurrentCategory,
  resetFilesStream,
  hasNoItems,
  hasMoreItems,
  isSecure,
  title,
  isLoading,
  areNextFilesLoading,
  itemsFilters,
  onDropOnFolder,
  mediaTypes,
  onFolderDoubleClick,
  onFileDoubleClick,
  clearItemsSelection,
  loadMoreItems,
  isItemEditing,
  isItemCut,
  isItemSelected,
  onItemClick,
  onItemRightClick,
  onEditItemStart,
  onEditItemCancel,
  onEditItemComplete,
  onItemNameChange,
  openExpandedView,
  selectAllMediaTypes,
  buyItem,
  clearAllExtendedFilters,
  refreshFolder,
  beginDrag,
  selectItem,
  selectItems,
  layout,
  onRemoveItemsFromBoard,
}) => {
  const { isNotAuthorized, isUnknownLoadingError } = useLoadingErrorCatcher();
  const { trackActionsOpenClick } = useTrackActions();
  const { trackItemsSelected } = useTrackItemSelectAnalytics();
  const { isMediaFromWix, isShutterstock, isFavorites, isTrash, isBoardsRoot, isEnterprise } = useSources({
    source,
    searchQuery,
  });

  const { value: isLassoSelecting, setTrue: setLassoSelecting, setFalse: resetLassoSelecting } = useBoolean(false);
  const LayoutComponent = isBoardsRoot ? BoardsLayout : layout === Layout.Gallery ? GalleryLayout : ListLayout;
  const canDragItems = !isFavorites && !isTrash;

  const handleLassoSelectFinish = useCallback(
    (itemIds: string[]) => {
      resetLassoSelecting();
      const itemsToTrack = innerJoin((item, id) => item.id === id, items, itemIds);
      trackItemsSelected(itemsToTrack, { clickOrigin: ItemSelectOrigin.Lasso });
    },
    [trackItemsSelected, resetLassoSelecting, items],
  );

  const getParamsForCurrentFolderContextMenu = useCallback(() => {
    trackActionsOpenClick([currentFolder], ActionClickOrigin.RightClick, ActionTarget.Empty);

    return { contextMenuOrigin: ActionClickOrigin.RightClick };
  }, [trackActionsOpenClick, currentFolder]);

  const handleLayoutComponentClick = useCallback(() => {
    if (isLassoSelecting) {
      return;
    }

    clearItemsSelection();
  }, [isLassoSelecting, clearItemsSelection]);

  return (
    <div className={styles.contextMenuWrapper}>
      <div className={styles.content} data-hook="files-manager-content">
        <ContextMenuTrigger
          id={ContextMenuId.CurrentFolder}
          attributes={contextMenuTriggerAttributes}
          holdToDisplay={-1}
          disable={isNotAuthorized}
          collect={getParamsForCurrentFolderContextMenu}
        >
          {hasNoItems && currentFolder ? (
            areNextFilesLoading ? (
              <div className={styles.loader}>
                <FullHeightLoader />
              </div>
            ) : (
              <EmptyState
                refreshFolder={refreshFolder}
                isNotAuthorized={isNotAuthorized}
                isUnknownError={isUnknownLoadingError}
                isEnterprise={isEnterprise}
                currentFolder={currentFolder}
                source={source}
                settingsLayout={settingsLayout}
                searchQuery={searchQuery}
                className={styles.emptyState}
                selectAllMediaTypes={selectAllMediaTypes}
                clearAllExtendedFilters={clearAllExtendedFilters}
                mediaTypes={mediaTypes}
                extendedFiltersElement={
                  (isMediaFromWix || isShutterstock || isEnterprise) &&
                  !isNotAuthorized && (
                    <ExtendedItemsFilters
                      sourceName={isEnterprise ? currentFolder.name : undefined}
                      title={title}
                      customFilters={itemsFilters}
                      isLoading={isLoading}
                      searchQuery={searchQuery}
                      source={source}
                    />
                  )
                }
                itemsFilters={itemsFilters}
                dataHook="files-manager-empty-state"
              />
            )
          ) : (
            <Lasso
              itemMarginRight={LassoItemMargin[layout]}
              selectedItems={selectedItems}
              onFirstMove={setLassoSelecting}
              onSelectItems={selectItems}
              onSelectFinish={handleLassoSelectFinish}
            >
              <LayoutComponent
                className={classnames(styles.layout, isLoading && styles.loading)}
                isLoading={isLoading}
                hasMoreItems={hasMoreItems}
                onClick={handleLayoutComponentClick}
                onContextMenu={clearItemsSelection}
                loadMoreItems={loadMoreItems}
                resetFilesStream={resetFilesStream}
                itemsCount={items.length}
                searchQuery={searchQuery}
                extendedFiltersElement={
                  (isMediaFromWix || isShutterstock || isEnterprise) && (
                    <ExtendedItemsFilters
                      sourceName={isEnterprise ? currentFolder.name : undefined}
                      title={title}
                      customFilters={itemsFilters}
                      isLoading={isLoading}
                      searchQuery={searchQuery}
                      source={source}
                    />
                  )
                }
                selectItem={selectItem}
              >
                {({ File, Folder, Board, LoadingIndicator, itemClassName, boardCardExternalClassName }) => [
                  ...map((item) => {
                    const { id } = item;
                    const isEditing = isItemEditing(id);
                    const isSelected = isItemSelected(id) && !isEditing;
                    const isCut = isItemCut(id);
                    const selectedItemsForCurrentItem = isSelected ? selectedItems : null;

                    /** also checking if corresponding component exists because
                     * layout change and items update are not synchronized.
                     * It's trying to render board items into gallery layout and vice versa.
                     * Might be fixed when withItemsLoader is refactored to hooks
                     */
                    return Board && isBoard(item) ? (
                      <Board
                        key={item.id}
                        item={item}
                        className={itemClassName}
                        beginDrag={beginDrag}
                        isEditing={isEditing}
                        selected={isSelected}
                        onClick={onItemClick}
                        onDoubleClick={onFolderDoubleClick}
                        onContextMenu={onItemRightClick}
                        canDrag={canDragItems}
                        selectedItems={selectedItemsForCurrentItem}
                        currentFolder={currentFolder}
                        boardCardExternalClassName={boardCardExternalClassName}
                      />
                    ) : File && isFile(item) ? (
                      <File
                        key={item.id}
                        className={itemClassName}
                        canDrag={canDragItems}
                        item={(isEditing ? editingItem : item) as MediaManagerSDKFile}
                        selectedItems={selectedItemsForCurrentItem}
                        selected={isSelected}
                        publicMediaCurrentCategory={publicMediaCurrentCategory}
                        isCut={isCut}
                        isEditing={isEditing}
                        isSecure={isSecure}
                        onClick={onItemClick}
                        onDoubleClick={onFileDoubleClick}
                        onContextMenu={onItemRightClick}
                        onNameChange={onItemNameChange}
                        onRenameStart={onEditItemStart}
                        onRenameComplete={onEditItemComplete}
                        onRenameCancel={onEditItemCancel}
                        openExpandedView={openExpandedView}
                        isFavorite={isItemFavorite(item)}
                        buyItem={buyItem}
                        beginDrag={beginDrag}
                        currentFolder={currentFolder}
                        onRemoveItemsFromBoard={onRemoveItemsFromBoard}
                        source={source}
                      />
                    ) : Folder ? (
                      <Folder
                        key={item.id}
                        className={itemClassName}
                        canDrag={canDragItems}
                        item={(isEditing ? editingItem : item) as MediaManagerSDKFolder}
                        selectedItems={selectedItemsForCurrentItem}
                        selected={isSelected}
                        isCut={isCut}
                        isEditing={isEditing}
                        isSecure={isSecure}
                        onClick={onItemClick}
                        onContextMenu={onItemRightClick}
                        onNameChange={onItemNameChange}
                        onRenameStart={onEditItemStart}
                        onRenameComplete={onEditItemComplete}
                        onRenameCancel={onEditItemCancel}
                        onDoubleClick={onFolderDoubleClick}
                        onDrop={onDropOnFolder}
                        isFavorite={isItemFavorite(item)}
                        beginDrag={beginDrag}
                        currentFolder={currentFolder}
                        onRemoveItemsFromBoard={onRemoveItemsFromBoard}
                        source={source}
                      />
                    ) : null;
                  }, items),

                  areNextFilesLoading && <LoadingIndicator key="files-loading-indicator" />,
                ]}
              </LayoutComponent>
            </Lasso>
          )}
        </ContextMenuTrigger>
      </div>
    </div>
  );
};

export const SourceContent: FC<IOuter> = memo<IOuter>(SourceContentComponent);
